import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { NavLink } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';
import {
  LIVE_BADGE_CATEGORY_KEY,
  LIVE_BADGE_GROUPS,
  LIVE_BADGE_PRODUCT_TYPE,
  LIVE_BADGE_SECTIONS,
  LIVE_BADGE_SLOTS,
  LIVE_BADGE_UPLOAD_RULES,
  LiveBadgeSection,
  LiveBadgeSlot,
  PillColors,
  checkLiveBadgeFile,
  normalizeHex,
  parsePillColors,
} from '../data/liveBadges';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

type SavedIcon = { Url: string; MimeType?: string; Width?: number; Height?: number };
type SavedBadge = { icon?: SavedIcon; colors?: PillColors };
type ColorDraft = { start: string; end: string; text: string };

const COLOR_FIELDS: Array<{ key: keyof PillColors; label: string }> = [
  { key: 'start', label: 'Gradient start' },
  { key: 'end', label: 'Gradient end' },
  { key: 'text', label: 'Text colour' },
];

const measure = (file: File) =>
  new Promise<{ Width: number; Height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ Width: image.naturalWidth, Height: image.naturalHeight });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read this image.'));
    };
    image.src = url;
  });

/** The chat pill as the app draws it (16 px tall, 14 px icon, 9 pt label), at 3x. */
const PillPreview: React.FC<{ slot: LiveBadgeSlot; iconUrl?: string; colors: PillColors }> = ({ slot, iconUrl, colors }) => (
  <div
    className='inline-flex items-center rounded-full'
    style={{
      height: 48,
      padding: '0 15px 0 6px',
      background: `linear-gradient(135deg, ${colors.start}, ${colors.end})`,
      border: `2px solid ${colors.end}`,
      boxShadow: `0 0 15px ${colors.end}73`,
    }}
  >
    <span className='flex h-[42px] w-[42px] items-center justify-center'>
      {iconUrl ? (
        <img src={iconUrl} alt='' className='h-[42px] w-[42px] object-contain' />
      ) : (
        <span className='text-2xl' aria-hidden>
          {slot.section === 'top-fan' ? '👑' : slot.section === 'daily-streak' ? '🔥' : '★'}
        </span>
      )}
    </span>
    <span className='ml-1.5 text-[27px] font-bold leading-none' style={{ color: colors.text }}>
      {slot.previewLabel}
    </span>
  </div>
);

const ColorEditor: React.FC<{
  draft: ColorDraft;
  saved: boolean;
  busy: boolean;
  onChange: (next: ColorDraft) => void;
  onSave: () => void;
  onReset: () => void;
}> = ({ draft, saved, busy, onChange, onSave, onReset }) => (
  <div className='rounded-xl border border-white/10 bg-black/20 p-3'>
    <div className='mb-2 flex items-center justify-between'>
      <span className='text-xs font-semibold text-white'>Pill colours</span>
      <span className={`text-[10px] ${saved ? 'text-emerald-300' : 'text-gray-500'}`}>{saved ? 'Custom' : 'App default'}</span>
    </div>
    <div className='space-y-2'>
      {COLOR_FIELDS.map(({ key, label }) => {
        const valid = normalizeHex(draft[key]);
        return (
          <label key={key} className='flex items-center gap-2 text-xs text-gray-300'>
            <span className='w-24 shrink-0'>{label}</span>
            <input
              type='color'
              aria-label={`${label} picker`}
              value={valid || '#000000'}
              disabled={busy}
              onChange={(event) => onChange({ ...draft, [key]: event.target.value.toUpperCase() })}
              className='h-7 w-9 cursor-pointer rounded border border-white/15 bg-transparent'
            />
            <input
              type='text'
              aria-label={label}
              value={draft[key]}
              disabled={busy}
              maxLength={7}
              onChange={(event) => onChange({ ...draft, [key]: event.target.value })}
              className={`w-24 rounded-md border bg-slate-900 px-2 py-1 font-mono text-xs text-white ${
                valid ? 'border-white/15' : 'border-red-500'
              }`}
            />
          </label>
        );
      })}
    </div>
    <div className='mt-3 flex gap-2'>
      <button
        type='button'
        disabled={busy}
        onClick={onSave}
        className='flex-1 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-white disabled:opacity-50'
      >
        {busy ? 'Saving...' : 'Save colours'}
      </button>
      {saved && (
        <button
          type='button'
          disabled={busy}
          onClick={onReset}
          className='rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-gray-200 hover:bg-white/10 disabled:opacity-50'
        >
          Reset
        </button>
      )}
    </div>
  </div>
);

const LiveBadges: React.FC<{ section: LiveBadgeSection }> = ({ section }) => {
  const [badges, setBadges] = useState<Record<string, SavedBadge>>({});
  const [drafts, setDrafts] = useState<Record<string, ColorDraft>>({});
  const [rankArt, setRankArt] = useState<Record<string, string>>({});
  const rankArtRef = React.useRef<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const info = LIVE_BADGE_SECTIONS[section];
  const slots = LIVE_BADGE_SLOTS.filter((slot) => slot.section === section);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const listProducts = useCallback(async (headers: Record<string, string>, userId: string, CategoryKey: string) => {
    const response = await fetch(`${walletApiBase}/getWalletShopProductsV2`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ UserID: userId, CategoryKey, Type: LIVE_BADGE_PRODUCT_TYPE, IncludeInactive: true, Environment: 'prod' }),
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.message || 'Failed to load live badges');
    return (json.data?.products || []) as Array<{
      ProductKey: string;
      MediaAssets?: Array<SavedIcon & { Type: string }>;
      Metadata?: { PillColors?: unknown; LiveQuestRewardId?: string };
    }>;
  }, []);

  const loadBadges = useCallback(async () => {
    const headers = authHeaders();
    const userId = (localStorage.getItem('wallet_assets_user_id') || '').trim();
    if (!headers || !userId) {
      if (!userId) toast.error('Your account ID is missing. Sign out and back in.');
      return;
    }
    setLoading(true);
    try {
      const [products, quest] = await Promise.all([
        listProducts(headers, userId, LIVE_BADGE_CATEGORY_KEY),
        section === 'pingtop-level'
          ? listProducts(headers, userId, 'LIVE_QUEST').catch(() => [])
          : Promise.resolve([]),
      ]);

      if (section === 'pingtop-level') {
        // Rank artwork lives on the Live Quest page (LIVE_QUEST utility products, reward id rank_<id>).
        const art: Record<string, string> = {};
        for (const product of quest) {
          const rewardId = product.Metadata?.LiveQuestRewardId || '';
          const url = (product.MediaAssets || []).find((media) => media.Type === 'thumbnail' || media.Type === 'image')?.Url;
          if (rewardId.startsWith('rank_') && url) art[rewardId.slice('rank_'.length)] = url;
        }
        rankArtRef.current = art;
        setRankArt(art);
      }

      const next: Record<string, SavedBadge> = {};
      for (const product of products) {
        const slot = LIVE_BADGE_SLOTS.find((s) => s.productKey === product.ProductKey);
        const image = (product.MediaAssets || []).find((media) => media.Type === 'image');
        const colors = parsePillColors(product.Metadata?.PillColors);
        // Only mark icon as custom when the slot supports icon uploads and the URL isn't just the default/rank fallback.
        const isCustomIcon =
          slot?.kind !== 'colors_only' &&
          Boolean(image?.Url && image.Url !== slot?.defaultUrl && !image.Url.includes('/assets/live/badges/'));
        next[product.ProductKey] = {
          ...(isCustomIcon && image?.Url
            ? { icon: { Url: image.Url, MimeType: image.MimeType, Width: image.Width, Height: image.Height } }
            : {}),
          ...(colors ? { colors } : {}),
        };
      }
      setBadges(next);
      setDrafts({});
    } catch (error) {
      toast.error((error as Error).message || 'Failed to load live badges');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, listProducts, section]);

  useEffect(() => {
    loadBadges();
  }, [loadBadges]);

  /** Saves the badge record with this icon and these colours. Inactive: never sold in the shop. */
  const saveBadge = async (headers: Record<string, string>, slot: LiveBadgeSlot, next: SavedBadge) => {
    const fallbackRankUrl = slot.rankId
      ? rankArtRef.current[slot.rankId] || rankArt[slot.rankId] || `https://cdn2.pingtop.co/assets/live/badges/ranks/${slot.rankId}.png`
      : undefined;
    // getWalletShopProductsV2 filters out products with empty MediaAssets. Always supply an image asset
    // (uploaded icon, rank artwork from Live Quest, built-in fallback, or rank fallback) so the saved
    // record is returned by the API and custom colours persist on reload.
    const mediaUrl =
      next.icon?.Url ||
      (slot.kind === 'colors_only' ? fallbackRankUrl : undefined) ||
      slot.defaultUrl ||
      fallbackRankUrl;

    const mediaAssets = next.icon
      ? [{ Type: 'image', Url: next.icon.Url, MimeType: next.icon.MimeType, Width: next.icon.Width, Height: next.icon.Height }]
      : mediaUrl
        ? [{ Type: 'image', Url: mediaUrl, MimeType: 'image/png' }]
        : [];

    const response = await fetch(`${walletApiBase}/createWalletShopProduct`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ProductKey: slot.productKey,
        Name: `Live badge: ${slot.title}`,
        Description: slot.description,
        CategoryKey: LIVE_BADGE_CATEGORY_KEY,
        Type: LIVE_BADGE_PRODUCT_TYPE,
        AvailabilityType: 'unlimited',
        CurrencyType: 'coin',
        PriceCoins: 0,
        DisplayOrder: slot.order,
        IsActive: false,
        Environment: 'prod',
        Tags: ['live_badge', slot.section.replace(/-/g, '_'), slot.group.replace(/-/g, '_')],
        MediaAssets: mediaAssets,
        Metadata: { LiveBadge: slot.section, LiveBadgeGroup: slot.group, ...(next.colors ? { PillColors: next.colors } : {}) },
      }),
    });
    const json = await response.json();
    if (!response.ok || json?.status === false) throw new Error(json?.message || 'Failed to save the badge');
  };

  const run = async (slot: LiveBadgeSlot, work: (headers: Record<string, string>) => Promise<string>) => {
    const headers = authHeaders();
    if (!headers) return;
    setBusyKey(slot.productKey);
    try {
      const message = await work(headers);
      await loadBadges();
      toast.success(message);
    } catch (error) {
      toast.error((error as Error).message || 'Could not save the badge');
    } finally {
      setBusyKey('');
    }
  };

  const upload = (slot: LiveBadgeSlot, file: File) => {
    const problem = checkLiveBadgeFile(file, slot.kind);
    if (problem) {
      toast.error(problem);
      return;
    }
    run(slot, async (headers) => {
      const size = await measure(file);
      if (slot.kind === 'pill_icon' && size.Width !== size.Height) {
        throw new Error(`Use a square image (this one is ${size.Width}×${size.Height}).`);
      }
      const presign = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ProductKey: slot.productKey,
          ContentType: file.type,
          AssetType: 'image',
          AssetKind: 'live_badge',
          FileSize: file.size,
          ...size,
        }),
      });
      const presignJson = await presign.json();
      if (!presign.ok) throw new Error(presignJson.message || 'Failed to prepare the upload');
      const { UploadUrl, AssetUrl } = presignJson.data;
      const put = await fetch(UploadUrl, { method: 'PUT', body: await file.arrayBuffer(), headers: { 'Content-Type': file.type } });
      if (!put.ok) throw new Error('Failed to upload the file to storage');
      await saveBadge(headers, slot, { ...badges[slot.productKey], icon: { Url: AssetUrl, MimeType: file.type, ...size } });
      return `${slot.title} updated. The app shows it within a minute.`;
    });
  };

  const removeIcon = (slot: LiveBadgeSlot) => {
    if (!window.confirm(`Remove the ${slot.title} image? The app goes back to ${slot.fallback}.`)) return;
    run(slot, async (headers) => {
      await saveBadge(headers, slot, { colors: badges[slot.productKey]?.colors });
      return `${slot.title} image removed`;
    });
  };

  const saveColors = (slot: LiveBadgeSlot, draft: ColorDraft) => {
    const start = normalizeHex(draft.start);
    const end = normalizeHex(draft.end);
    const text = normalizeHex(draft.text);
    if (!start || !end || !text) {
      toast.error('Use 6-digit hex colours like #FFF9EB.');
      return;
    }
    run(slot, async (headers) => {
      await saveBadge(headers, slot, { icon: badges[slot.productKey]?.icon, colors: { start, end, text } });
      return `${slot.title} colours saved. The app shows them within a minute.`;
    });
  };

  const resetColors = (slot: LiveBadgeSlot) => {
    if (!window.confirm(`Reset the ${slot.title} colours to the app defaults?`)) return;
    run(slot, async (headers) => {
      await saveBadge(headers, slot, { icon: badges[slot.productKey]?.icon });
      return `${slot.title} colours reset`;
    });
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />
      <div className='mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8'>
        <div className='mb-6'>
          <h1 className='text-2xl font-bold text-white sm:text-3xl'>Live Badges</h1>
          <p className='mt-1 text-sm text-gray-400'>
            Artwork and pill colours for the badges in live chat and on the Top Fans leaderboard. Changes reach the app within a minute; no
            app release needed.
          </p>
        </div>

        <div className='mb-6 flex flex-wrap gap-2'>
          {(Object.keys(LIVE_BADGE_SECTIONS) as LiveBadgeSection[]).map((key) => (
            <NavLink
              key={key}
              to={LIVE_BADGE_SECTIONS[key].path}
              className={({ isActive }) =>
                `rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  isActive ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`
              }
            >
              {LIVE_BADGE_SECTIONS[key].title}
            </NavLink>
          ))}
        </div>

        <h2 className='text-lg font-semibold text-white'>{info.title}</h2>
        <p className='mb-6 mt-1 text-sm text-gray-400'>{info.intro}</p>

        {loading ? (
          <div className='text-sm text-gray-400'>Loading current badges...</div>
        ) : (
          LIVE_BADGE_GROUPS.filter((group) => group.section === section).map((group) => {
            const groupSlots = slots.filter((slot) => slot.group === group.id);
            return (
              <section key={group.id} className='mb-10'>
                <h3 className='text-base font-semibold text-white'>{group.title}</h3>
                <p className='mt-1 text-sm text-gray-400'>{group.intro}</p>
                <p className='mb-4 mt-1 text-xs text-gray-500'>{LIVE_BADGE_UPLOAD_RULES[groupSlots[0]?.kind || 'pill_icon']}</p>
                <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                  {groupSlots.map((slot) => {
                    const saved = badges[slot.productKey] || {};
                    const busy = busyKey === slot.productKey;
                    const appColors = saved.colors || slot.defaultColors;
                    const draft = drafts[slot.productKey] || {
                      start: appColors?.start || '',
                      end: appColors?.end || '',
                      text: appColors?.text || '',
                    };
                    const draftColors = parsePillColors(draft) || appColors;
                    const artUrl = slot.kind === 'colors_only' ? (slot.rankId ? rankArt[slot.rankId] : undefined) : saved.icon?.Url || slot.defaultUrl;
                    return (
                      <article key={slot.productKey} className='overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-lg'>
                        <div className='flex min-h-40 flex-col items-center justify-center gap-3 bg-black/30 p-4'>
                          {slot.kind === 'badge' && artUrl && (
                            <div className='flex flex-col items-center gap-1'>
                              <img src={artUrl} alt={slot.title} className='h-20 w-20 object-contain' />
                              {!saved.icon && (
                                <span className='rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-gray-300'>Built-in</span>
                              )}
                            </div>
                          )}
                          {draftColors ? (
                            <PillPreview slot={slot} iconUrl={slot.kind === 'pill_icon' ? saved.icon?.Url : artUrl} colors={draftColors} />
                          ) : (
                            <span className='text-[11px] text-gray-500'>Pill colours: same as the Monthly badge</span>
                          )}
                          {draftColors && <span className='text-[11px] text-gray-500'>Chat pill preview, 3× size</span>}
                        </div>
                        <div className='space-y-3 p-4'>
                          <div>
                            <h4 className='text-sm font-semibold text-white'>{slot.title}</h4>
                            <p className='text-xs text-gray-400'>{slot.description}</p>
                            <p className='mt-1 font-mono text-[10px] text-gray-600'>{slot.productKey}</p>
                          </div>

                          {slot.kind !== 'colors_only' && (
                            <div className='space-y-2'>
                              {saved.icon ? (
                                <a
                                  href={saved.icon.Url}
                                  target='_blank'
                                  rel='noreferrer'
                                  className='block break-all text-xs text-[#FF9C15] hover:underline'
                                >
                                  {saved.icon.Url}
                                </a>
                              ) : (
                                <p className='text-xs text-gray-600'>Not uploaded: the app shows {slot.fallback}.</p>
                              )}
                              <div className='flex gap-2'>
                                <label
                                  className={`block flex-1 cursor-pointer rounded-lg px-3 py-2 text-center text-xs font-semibold transition ${
                                    busyKey
                                      ? 'cursor-not-allowed bg-white/10 text-gray-400'
                                      : 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90'
                                  }`}
                                >
                                  {busy ? 'Saving...' : saved.icon ? 'Replace image' : slot.kind === 'pill_icon' ? 'Upload icon' : 'Upload new badge'}
                                  <input
                                    type='file'
                                    accept='image/png,image/webp'
                                    className='hidden'
                                    disabled={!!busyKey}
                                    onChange={(event) => {
                                      const file = event.target.files?.[0];
                                      event.target.value = '';
                                      if (file) upload(slot, file);
                                    }}
                                  />
                                </label>
                                {saved.icon && (
                                  <button
                                    type='button'
                                    disabled={!!busyKey}
                                    onClick={() => removeIcon(slot)}
                                    className='rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-gray-200 hover:bg-white/10 disabled:opacity-50'
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          {slot.kind === 'colors_only' && !artUrl && (
                            <p className='text-xs text-gray-600'>No rank artwork on the Live Quest page yet: the app shows a star.</p>
                          )}

                          {slot.colors && (
                            <ColorEditor
                              draft={draft}
                              saved={!!saved.colors}
                              busy={!!busyKey}
                              onChange={(next) => setDrafts((current) => ({ ...current, [slot.productKey]: next }))}
                              onSave={() => saveColors(slot, draft)}
                              onReset={() => resetColors(slot)}
                            />
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LiveBadges;
